// src/Ordenes.js
import React, { useEffect, useState } from 'react';
import { supabase } from './supabaseClient';

const borde = '#e5e7eb';
const negro = '#111827';
const naranja = '#f97316';

function Ordenes() {
  const [ordenes, setOrdenes] = useState([]);
  const [busqueda, setBusqueda] = useState('');
  const [filtroEstado, setFiltroEstado] = useState('en_proceso');
  const [mensaje, setMensaje] = useState('');
  const [actualizandoId, setActualizandoId] = useState(null);
  const [montoEditando, setMontoEditando] = useState({});

  const cargarOrdenes = async () => {
    const { data, error } = await supabase
      .from('ordenes_trabajo')
      .select(`
        id,
        descripcion_falla,
        observaciones,
        estado,
        fecha_ingreso,
        fecha_entrega,
        monto,
        clientes:cliente_id ( id, nombre ),
        vehiculos:vehiculo_id ( id, patente, marca, modelo, anio )
      `)
      .order('fecha_ingreso', { ascending: false });

    if (error) {
      console.error(error);
      setMensaje('Error al cargar órdenes: ' + error.message);
      return;
    }
    setOrdenes(data);
  };

  useEffect(() => {
    cargarOrdenes();
  }, []);

  const cambiarEstadoOrden = async (ordenId, nuevoEstado) => {
    try {
      setMensaje('');
      setActualizandoId(ordenId);

      const { error } = await supabase
        .from('ordenes_trabajo')
        .update({ estado: nuevoEstado })
        .eq('id', ordenId);

      if (error) {
        console.error(error);
        setMensaje('Error al actualizar estado: ' + error.message);
        return;
      }

      setOrdenes((prev) =>
        prev.map((o) =>
          o.id === ordenId ? { ...o, estado: nuevoEstado } : o
        )
      );
    } finally {
      setActualizandoId(null);
    }
  };

  const cambiarMontoOrden = async (ordenId) => {
    const valor = montoEditando[ordenId];
    const montoNumero = valor === '' || valor == null ? null : Number(valor);

    try {
      setMensaje('');
      setActualizandoId(ordenId);

      const { error } = await supabase
        .from('ordenes_trabajo')
        .update({ monto: montoNumero })
        .eq('id', ordenId);

      if (error) {
        console.error(error);
        setMensaje('Error al actualizar monto: ' + error.message);
        return;
      }

      setOrdenes((prev) =>
        prev.map((o) =>
          o.id === ordenId ? { ...o, monto: montoNumero } : o
        )
      );
    } finally {
      setActualizandoId(null);
    }
  };

  const ordenesFiltradas = ordenes.filter((o) => {
    if (filtroEstado && o.estado !== filtroEstado) return false;

    const term = busqueda.toLowerCase();
    if (!term) return true;

    const clienteNombre = o.clientes?.nombre || '';
    const vehiculoPatente = o.vehiculos?.patente || '';
    const vehiculoModelo = o.vehiculos?.modelo || '';
    const falla = o.descripcion_falla || '';

    return (
      clienteNombre.toLowerCase().includes(term) ||
      vehiculoPatente.toLowerCase().includes(term) ||
      vehiculoModelo.toLowerCase().includes(term) ||
      falla.toLowerCase().includes(term)
    );
  });

  return (
    <section style={{ marginTop: 8 }}>
      <h3 style={{ marginTop: 0, marginBottom: 12, color: negro }}>
        Órdenes de trabajo
      </h3>

      {/* Buscador + filtro */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <input
          placeholder="Buscar por cliente, patente, modelo o falla"
          value={busqueda}
          onChange={(e) => setBusqueda(e.target.value)}
          style={{
            flex: 1,
            minWidth: 220,
            padding: '6px 8px',
            borderRadius: 8,
            border: `1px solid ${borde}`,
          }}
        />

        <select
          value={filtroEstado}
          onChange={(e) => setFiltroEstado(e.target.value)}
          style={{
            padding: '6px 8px',
            borderRadius: 8,
            border: `1px solid ${borde}`,
          }}
        >
          <option value="">Todos</option>
          <option value="pendiente">Pendiente</option>
          <option value="en_proceso">En proceso</option>
          <option value="terminado">Terminado</option>
        </select>
      </div>

      {mensaje && <p>{mensaje}</p>}

      {/* Encabezado tipo tabla */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1.2fr 1.6fr 2fr 1fr 1.1fr',
          gap: 8,
          padding: '8px 12px',
          fontSize: 12,
          fontWeight: 600,
          color: '#4b5563',
          borderBottom: '1px solid #e5e7eb',
        }}
      >
        <span>Cliente</span>
        <span>Vehículo</span>
        <span>Falla</span>
        <span>Monto</span>
        <span style={{ textAlign: 'right' }}>Estado</span>
      </div>

      {/* Filas */}
      <div>
        {ordenesFiltradas.map((o) => (
          <div
            key={o.id}
            style={{
              display: 'grid',
              gridTemplateColumns: '1.2fr 1.6fr 2fr 1fr 1.1fr',
              gap: 8,
              padding: '8px 10px',
              borderBottom: `1px solid ${borde}`,
              alignItems: 'center',
              fontSize: 13,
            }}
          >
            {/* Cliente */}
            <div style={{ fontWeight: 600 }}>
              {o.clientes?.nombre || 'Sin nombre'}
            </div>

            {/* Vehículo */}
            <div style={{ color: '#4b5563' }}>
              {(o.vehiculos?.patente || 'Sin patente') +
                ' · ' +
                (o.vehiculos?.marca || '') +
                ' ' +
                (o.vehiculos?.modelo || '')}
            </div>

            {/* Falla */}
            <div>{o.descripcion_falla}</div>

            {/* Monto editable */}
            <div>
              <input
                type="number"
                min="0"
                style={{
                  width: '100%',
                  padding: '3px 6px',
                  borderRadius: 6,
                  border: `1px solid ${borde}`,
                  fontSize: 12,
                }}
                value={
                  montoEditando[o.id] !== undefined
                    ? montoEditando[o.id]
                    : (o.monto ?? '')
                }
                onChange={(e) =>
                  setMontoEditando((prev) => ({
                    ...prev,
                    [o.id]: e.target.value,
                  }))
                }
              />
              <button
                type="button"
                onClick={() => cambiarMontoOrden(o.id)}
                disabled={actualizandoId === o.id}
                style={{
                  marginTop: 4,
                  padding: '2px 8px',
                  borderRadius: 999,
                  border: 'none',
                  backgroundColor: naranja,
                  color: 'white',
                  fontSize: 11,
                  cursor: 'pointer',
                }}
              >
                {actualizandoId === o.id ? 'Guardando…' : 'Guardar'}
              </button>
            </div>

            {/* Estado */}
            <div style={{ textAlign: 'right' }}>
              <select
                value={o.estado}
                onChange={(e) => cambiarEstadoOrden(o.id, e.target.value)}
                disabled={actualizandoId === o.id}
                style={{
                  padding: '3px 6px',
                  borderRadius: 999,
                  border: `1px solid ${borde}`,
                  fontSize: 12,
                }}
              >
                <option value="pendiente">Pendiente</option>
                <option value="en_proceso">En proceso</option>
                <option value="terminado">Terminado</option>
              </select>
              {actualizandoId === o.id && (
                <span style={{ marginLeft: 4, fontSize: 11 }}>Guardando…</span>
              )}
            </div>
          </div>
        ))}

        {ordenesFiltradas.length === 0 && (
          <p style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>
            No hay órdenes con ese filtro.
          </p>
        )}
      </div>
    </section>
  );
}

export default Ordenes;
